"use client";

import { motion } from "framer-motion";
import { FiCheck } from "react-icons/fi";
import Card from "@/components/ui/Card";
import Button from "@/components/ui/Button";
import Container from "@/components/ui/Container";

const pricingPlans = [
  {
    name: "Basic",
    price: "$49",
    period: "/Month",
    description: "Perfecto para pequeños productores que están empezando",
    features: [
      "Hasta 5 hectáreas",
      "Mapas interactivos básicos",
      "Captura de datos en campo",
      "Sincronización automática",
      "Reportes básicos",
      "1 usuario"
    ],
    highlighted: false,
    ctaText: "Empezar ahora",
    ctaVariant: "primary" as const
  },
  {
    name: "Business",
    price: "$99",
    period: "/Month",
    description: "Para operaciones en crecimiento con múltiples lotes",
    features: [
      "Hasta 20 hectáreas",
      "Todo en Basic",
      "Gestión de sesiones de muestreo",
      "Geolocalización avanzada",
      "Reportes personalizados",
      "Hasta 5 usuarios",
      "Soporte prioritario"
    ],
    highlighted: false,
    ctaText: "Prueba gratis",
    ctaVariant: "primary" as const
  },
  {
    name: "Premium",
    price: "$149",
    period: "/Month",
    description: "Para operaciones profesionales con análisis avanzado",
    features: [
      "Hasta 100 hectáreas",
      "Todo en Business",
      "Análisis predictivo con IA",
      "Mapas de calor y visualizaciones",
      "Integración con GIS",
      "Hasta 15 usuarios",
      "API de integración",
      "Soporte 24/7"
    ],
    highlighted: true,
    ctaText: "Empezar ahora",
    ctaVariant: "accent" as const
  },
  {
    name: "Ultimate",
    price: "$199",
    period: "/Month",
    description: "Para grandes productores y cooperativas agrícolas",
    features: [
      "Hectáreas ilimitadas",
      "Todo en Premium",
      "Usuarios ilimitados",
      "Personalización completa",
      "Consultoría agronómica",
      "Capacitación del equipo",
      "Gestor de cuenta dedicado",
      "SLA garantizado"
    ],
    highlighted: false,
    ctaText: "Contactar ventas",
    ctaVariant: "primary" as const
  }
];

export default function Pricing() {
  return (
    <section id="pricing" className="py-20 bg-beige-light">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2
            className="mb-4"
            style={{
              alignSelf: 'stretch',
              color: '#242424',
              textAlign: 'center',
              fontSize: '50px',
              fontWeight: 400,
              lineHeight: '60px',
            }}
          >
            Plans & Pricing
          </h2>
          <p
            className="max-w-3xl mx-auto"
            style={{
              alignSelf: 'stretch',
              color: '#242424',
              textAlign: 'center',
              fontSize: '24px',
              fontWeight: 400,
              lineHeight: '28px',
            }}
          >
            Elige el plan perfecto para tu operación agrícola. Todos los planes incluyen
            prueba gratuita de 14 días sin compromiso.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {pricingPlans.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Card
                variant="elevated"
                padding="lg"
                className={`h-full relative ${
                  plan.highlighted
                    ? "border-2 border-accent ring-4 ring-accent/20 scale-105"
                    : ""
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-accent text-white px-4 py-1 rounded-full text-sm font-medium shadow-lg">
                      Más popular
                    </span>
                  </div>
                )}

                {/* Plan Name */}
                <h3
                  className="mb-2"
                  style={{
                    alignSelf: 'stretch',
                    color: '#242424',
                    fontSize: '24px',
                    fontWeight: 700,
                    lineHeight: '28px',
                  }}
                >
                  {plan.name}
                </h3>

                {/* Price */}
                <div className="mb-4">
                  <span
                    style={{
                      alignSelf: 'stretch',
                      color: '#242424',
                      fontFamily: 'Abril Fatface',
                      fontSize: '40px',
                      fontWeight: 400,
                      lineHeight: '50px',
                    }}
                  >
                    {plan.price}
                  </span>
                  <span className="text-gray-600">{plan.period}</span>
                </div>

                {/* Description */}
                <p className="text-gray-600 mb-6 min-h-[48px]">{plan.description}</p>

                {/* CTA Button */}
                <Button
                  variant={plan.ctaVariant}
                  size="md"
                  className="w-full mb-6"
                  style={{
                    color: '#FFF',
                    fontSize: '20px',
                    fontWeight: 700,
                    lineHeight: '26px',
                  }}
                >
                  {plan.ctaText}
                </Button>

                {/* Features List */}
                <ul className="space-y-3">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <FiCheck className="w-5 h-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                      <span
                        style={{
                          flex: '1 0 0',
                          color: '#242424',
                          fontSize: '20px',
                          fontWeight: 400,
                          lineHeight: '26px',
                        }}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
}
